import chunk from 'lodash/chunk';
import { fetchQuery, graphql } from 'react-relay';
import { log } from '@atlassian/jira-common-util-logging';
import { getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import getRelayEnvironment from '@atlassian/jira-relay-environment';
import type { servicesAssociatedIssuesDataQuery } from '@atlassian/jira-relay/src/__generated__/servicesAssociatedIssuesDataQuery.graphql';
import type { IssueId } from '@atlassian/jira-shared-types';
import type { AssociatedIssue } from '../common/types';

// this is the maximum number of issues that can be fetched in one request because AGG limits
const MAX_ISSUES = 50;

const fetchAssociatedIssuesChunk = (
	ids: string[],
	cloudId: string,
): Promise<Record<IssueId, AssociatedIssue>> =>
	fetchQuery<servicesAssociatedIssuesDataQuery>(
		getRelayEnvironment(),
		graphql`
			query servicesAssociatedIssuesDataQuery($cloudId: ID!, $jql: String!, $limit: Int!) {
				jira {
					issueSearchStable(cloudId: $cloudId, issueSearchInput: { jql: $jql }, first: $limit) {
						edges {
							node {
								issueId
								key
								summaryField {
									text
								}
								issueTypeField {
									issueType {
										avatar {
											medium
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			jql: `id in (${ids.join(',')}) AND projectType = product_discovery`,
			limit: MAX_ISSUES,
		},
	)
		.toPromise()
		.then((data) => {
			const associatedIssues: Record<IssueId, AssociatedIssue> = {};
			data?.jira?.issueSearchStable?.edges?.forEach((edge) => {
				const id = edge?.node?.issueId;
				const key = edge?.node?.key;
				const summary = edge?.node?.summaryField?.text;
				const iconUrl = edge?.node?.issueTypeField?.issueType?.avatar?.medium;
				if (!id || !key || !summary || !iconUrl) {
					log.safeErrorWithoutCustomerData(
						'portfolio-3.associated-issues.invalid-data',
						'Invalid data received.',
					);
					return;
				}
				associatedIssues[id] = { id, key, summary, iconUrl };
			});
			return associatedIssues;
		});

export const fetchAssociatedIssues = (
	ids: string[],
	cloudId: string,
): Promise<Record<IssueId, AssociatedIssue>> => {
	if (ids.length === 0) {
		return Promise.resolve({});
	}

	if (
		ids.length >
		MAX_ISSUES *
			getFeatureFlagValue<number>('polaris-limit-ideas-loaded-in-arj-plan-integration', 20)
	) {
		log.safeErrorWithoutCustomerData(
			'portfolio-3.associated-issues.limit-exceeded',
			'Ideas not loaded because limit exceeded.',
		);
		return Promise.resolve({});
	}
	return Promise.all(
		chunk(ids, MAX_ISSUES).map((idChunk) => fetchAssociatedIssuesChunk(idChunk, cloudId)),
	).then((results: Record<IssueId, AssociatedIssue>[]) => {
		const allAssociatedIssues: Record<IssueId, AssociatedIssue> = {};
		results.forEach((associatedIssues) => {
			Object.assign(allAssociatedIssues, associatedIssues);
		});
		return allAssociatedIssues;
	});
};
